import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InlineSvg } from "@/components/ui/inline-svg";
import { getWorkspaceIdWithFallback } from "@/lib/routing/workspace-context";
import { getCampaignServer } from "@/server/campaigns";
import { getReferralStatsServer } from "@/server/referrals";
import type { CampaignResponse } from "@/types/campaign";
import { toast } from "sonner";
import { logError } from "@/lib/structured-log";
import { Copy, Rocket } from "lucide-react";
import rocketSvg from "@/assets/icons/rocket.svg?raw";
import referral1Svg from "@/assets/icons/referral1.svg?raw";
import referral2Svg from "@/assets/icons/referral2.svg?raw";
import referral3Svg from "@/assets/icons/referral3.svg?raw";

export const Route = createFileRoute(
  "/workspace/$workspaceSlug/campaigns/$campaignId/launch-success",
)({
  loader: async ({ context, parentMatchPromise, params }) => {
    const workspaceId = await getWorkspaceIdWithFallback(
      context,
      "campaign.launch_success",
      parentMatchPromise,
    );

    const [campaign, referral] = await Promise.all([
      getCampaignServer({
        data: {
          workspaceId,
          campaignId: params.campaignId,
        },
      }),
      getReferralStatsServer().catch((error) => {
        logError("campaigns.launch_success.referrals_failed", {
          error: error instanceof Error ? error.message : "unknown-error",
        });
        return null;
      }),
    ]);

    return {
      workspaceId,
      campaign,
      referralLink: referral?.referral_link ?? null,
      referralCode: referral?.referral_code ?? null,
    };
  },
  component: CampaignLaunchSuccessPage,
});

function CampaignLaunchSuccessPage() {
  const { t } = useTranslation(["pages", "settings"]);
  const navigate = useNavigate();
  const { workspaceSlug, campaignId } = Route.useParams();
  const {
    campaign,
    referralLink: initialReferralLink,
    referralCode,
  } = Route.useLoaderData() as {
    campaign: CampaignResponse;
    workspaceId: string;
    referralLink: string | null;
    referralCode: string | null;
  };
  const isSourcingMode = campaign.agent_mode === "sourcing";
  const launchCopyPrefix = isSourcingMode
    ? "pages:campaigns.launchSuccessPage.sourcing"
    : "pages:campaigns.launchSuccessPage";

  const referralLink = useMemo<string>(() => {
    if (referralCode) {
      const origin =
        typeof window !== "undefined" ? window.location.origin : "http://localhost:3000";
      try {
        return new URL(`/signin?invite=${referralCode}`, origin).toString();
      } catch {
        return `${origin}/signin?invite=${referralCode}`;
      }
    }
    return initialReferralLink ?? "https://workus.ai";
  }, [initialReferralLink, referralCode]);

  const handleViewCampaign = () => {
    navigate({
      to: "/workspace/$workspaceSlug/campaigns/$campaignId",
      params: { workspaceSlug, campaignId },
    });
  };

  const handleCopyReferral = async () => {
    if (!referralLink) {
      return;
    }
    try {
      await navigator.clipboard.writeText(referralLink);
      toast.success(t("settings:workspace.referralsPage.copied"));
    } catch {
      toast.error(t("settings:workspace.referralsPage.copyFailed"));
    }
  };

  return (
    <div className="flex min-h-full flex-col bg-gradient-to-b from-[#F7FBFF] to-[#FFFFFF]">
      <div className="flex items-center justify-end px-6 py-6">
        <Button
          variant="default"
          className="rounded-full px-7 py-2 text-sm font-semibold bg-black text-white hover:bg-black/90"
          onClick={handleViewCampaign}
        >
          {t("pages:campaigns.launchSuccessPage.viewCampaign")}
        </Button>
      </div>

      <div className="flex flex-1 items-start justify-center px-20 pb-10">
        <div className="w-full space-y-4">
          <div className="space-y-3 text-center">
            <h1 className="text-[32px] font-semibold tracking-tight text-gray-900">
              {t(`${launchCopyPrefix}.title`)}
            </h1>
            <p className="text-muted-foreground">{t(`${launchCopyPrefix}.statusText`)}</p>
            <p className="mt-1 text-sm text-muted-foreground md:text-base">
              {t(`${launchCopyPrefix}.subtitle`)}
            </p>
          </div>

          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1.4fr)] lg:items-start">
            <div className="overflow-hidden rounded-[32px] bg-black/90 shadow-xl aspect-video">
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-700 via-slate-900 to-slate-800">
                <div className="flex flex-col items-center gap-3 text-center text-white/85">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10">
                    <Rocket className="h-6 w-6" />
                  </div>
                  <p className="text-base font-semibold">{t(`${launchCopyPrefix}.video.title`)}</p>
                  <p className="max-w-xs text-xs text-white/70">
                    {t(`${launchCopyPrefix}.video.subtitle`)}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6 p-6">
              <h2 className="text-lg font-semibold text-gray-900">
                {t(`${launchCopyPrefix}.nextTitle`)}
              </h2>
              <div className="space-y-4 text-sm text-gray-700">
                <div className="flex gap-3">
                  <div className="flex h-8 w-8 item-start">
                    <InlineSvg content={referral1Svg} />
                  </div>
                  <div className="space-y-1">
                    <p className="font-medium">{t(`${launchCopyPrefix}.points.learning.title`)}</p>
                    <p className="text-xs text-muted-foreground md:text-sm">
                      {t(`${launchCopyPrefix}.points.learning.description`)}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="mt-1 flex h-8 w-8 item-start">
                    <InlineSvg content={referral2Svg} />
                  </div>
                  <div className="space-y-1">
                    <p className="font-medium">{t(`${launchCopyPrefix}.points.outreach.title`)}</p>
                    <p className="text-xs text-muted-foreground md:text-sm">
                      {t(`${launchCopyPrefix}.points.outreach.description`)}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="mt-1 flex h-8 w-8 item-start">
                    <InlineSvg content={referral3Svg} />
                  </div>
                  <div className="space-y-1">
                    <p className="font-medium">
                      {t(`${launchCopyPrefix}.points.opportunities.title`)}
                    </p>
                    <p className="text-xs text-muted-foreground md:text-sm">
                      {t(`${launchCopyPrefix}.points.opportunities.description`)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className="rounded-[16px] border border-[#DFDFDF] p-6"
            style={{
              background:
                "linear-gradient(105deg, rgba(51, 255, 255, 0.24) -7.45%, rgba(255, 255, 255, 0) 18.36%), linear-gradient(202deg, rgba(51, 95, 255, 0.6) -38.24%, rgba(255, 255, 255, 0) 53.47%), #FFF",
              boxShadow: "0 1px 4px 0 rgba(224, 224, 224, 0.6)",
            }}
          >
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="space-y-3">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-gray-900">
                    {t(`${launchCopyPrefix}.referral.title`)}
                  </p>
                  <p className="max-w-xl text-xs text-muted-foreground md:text-sm">
                    {t(`${launchCopyPrefix}.referral.description`)}
                  </p>
                </div>
                <div className="flex w-full max-w-[600px] flex-row gap-3 md:items-center">
                  <Input
                    className="flex-1 rounded-xl w-[500px] h-[32px] bg-white px-3 py-2 text-xs text-black md:min-w-[320px] md:text-sm"
                    value={referralLink ?? ""}
                    placeholder={t(`${launchCopyPrefix}.referral.placeholder`)}
                  />
                  <Button
                    variant="outline"
                    className="rounded-xl h-[32px] px-4 text-sm"
                    onClick={handleCopyReferral}
                    disabled={!referralLink}
                  >
                    <Copy className="size-4" />
                    {t(`${launchCopyPrefix}.referral.copy`)}
                  </Button>
                </div>
              </div>
              <div className="mt-4 flex w-full justify-center md:mt-0 md:w-auto">
                <div className="flex h-16 w-16 items-center justify-center text-[#335FFF]">
                  <InlineSvg content={rocketSvg} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
